/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';

import {
  type CopyRequest,
  copySentSchema,
  defaultCopySchema,
  type DefaultCopy,
  reminderBatchSchema,
} from '@/api/agreements';
import { request } from '@/api/client';

/**
 * Packets: a checklist of agreements one person has to sign.
 *
 * A packet orchestrates; it never bypasses. Each step becomes an ordinary
 * agreement running the ordinary lifecycle.
 */
export const stepSchema = z.object({
  step_order: z.number(),
  workflow_id: z.string(),
  workflow_name: z.string(),
  agreement_id: z.string().nullable(),
  agreement_reference: z.string().nullable(),
  agreement_stage: z.string().nullable(),
  /**
   * `cancelled`: its document was cancelled, and its runner starts it again or
   * skips it (`PK-01`). `skipped` counts as done.
   */
  status: z.enum(['pending', 'in_progress', 'complete', 'skipped', 'cancelled']),
  hidden: z.boolean(),
  default_copy: defaultCopySchema.nullable().default(null),
  /** The team whose workflow this step is. */
  team_name: z.string().nullable().default(null),
  /** Who is preparing it, once it is started. */
  prepared_by: z.string().nullable().default(null),
  /**
   * Its template was deleted, so it cannot be started; its runner skips it
   * instead (ADW-5498).
   */
  template_retired: z.boolean().default(false),
});

export type PacketStep = z.infer<typeof stepSchema>;

export const packetSchema = z.object({
  id: z.string(),
  reference: z.string(),
  subject_name: z.string(),
  subject_role: z.string().nullable(),
  subject_email: z.string().nullable(),
  owner_name: z.string(),
  owner_initials: z.string(),
  /** Who runs it, so the handover picker leaves them out (`PK-07`). */
  owner_id: z.string().nullable().default(null),
  workflow_name: z.string(),
  stage: z.string(),
  started_at: z.string().nullable(),
  done: z.number(),
  total: z.number(),
  blocking: z.string().nullable(),
  /** The step to start now, or null while one is under way (`PKT-18`). */
  next_step_order: z.number().nullable().default(null),
  steps: z.array(stepSchema),
  /** When it was cancelled and by whom (`PKT-27`); null unless it was. */
  cancelled_at: z.string().nullable().default(null),
  cancelled_by_name: z.string().nullable().default(null),
  /** Why it was cancelled (`PKT-30`), shown on its page alone. */
  cancel_reason: z.string().nullable().default(null),
  /** Whether you may cancel it now. For the screen only: the server decides. */
  can_cancel: z.boolean().default(false),
  /** Whether you may hand it to a colleague now (`PKT-34`). The server decides. */
  can_hand_over: z.boolean().default(false),
  /**
   * Whether you run it: it is assigned to you, or you assigned it on. Assigning
   * adds a person and keeps whoever ran it (decided 22 October 2026).
   */
  you_run_it: z.boolean().default(false),
  /** Whether you may start its steps: you run it, or you administer. The server decides. */
  can_start_steps: z.boolean().default(false),
  /** Whether you assigned it to its owner now, and so run it beside them. */
  assigned_by_you: z.boolean().default(false),
  /** Whether a handover can also message them on Slack, directly (`PKT-39`). */
  can_message_on_slack: z.boolean().default(false),
});

export type Packet = z.infer<typeof packetSchema>;

export const packetWorkflowSchema = z.object({
  id: z.string(),
  key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  subject_kind: z.string().nullable(),
  steps: z.array(z.string()),
  step_workflows: z.array(
    z.object({
      workflow_id: z.string(),
      workflow_name: z.string(),
      default_copy: defaultCopySchema.nullable().default(null),
      /** Its template was deleted: no packet starts from this one (ADW-5498). */
      template_retired: z.boolean().default(false),
    }),
  ),
  packet_count: z.number(),
});

export type PacketWorkflow = z.infer<typeof packetWorkflowSchema>;

/** A bundle holds at most this many workflows; the server says the same. */
export const MAX_BUNDLE_STEPS = 20;

/** At most this many addresses on one copy; the server says the same. */
export const MAX_COPY_RECIPIENTS = 10;
export const MAX_COPY_SUBJECT = 200;
export const MAX_COPY_BODY = 4000;

export type BundleInput = {
  name: string;
  description?: string | undefined;
  /** In the order they are signed. */
  workflow_ids: string[];
  /** Each step's default copy, by workflow. Given, it replaces them all. */
  default_copies?: (DefaultCopy & { workflow_id: string })[] | undefined;
};

/** How each packet stage reads on screen. */
export const PACKET_STAGE_LABEL: Record<string, string> = {
  prepare: 'Preparing',
  signing: 'Out for signature',
  countersign: 'Awaiting countersignature',
  filed: 'Complete',
  cancelled: 'Cancelled',
};

export const packetsQuery = () =>
  queryOptions({
    queryKey: ['packets'] as const,
    queryFn: ({ signal }) =>
      request('/api/v1/packets', z.array(packetSchema), { signal }),
  });

export const packetQuery = (id: string) =>
  queryOptions({
    queryKey: ['packet', id] as const,
    queryFn: ({ signal }) => request(`/api/v1/packets/${id}`, packetSchema, { signal }),
  });

export const packetWorkflowsQuery = () =>
  queryOptions({
    queryKey: ['packet-workflows'] as const,
    queryFn: ({ signal }) =>
      request('/api/v1/packet-workflows', z.array(packetWorkflowSchema), { signal }),
  });

export const startPacket = (body: {
  packet_workflow_id: string;
  subject_name: string;
  subject_role?: string | undefined;
  subject_email: string;
}) => request('/api/v1/packets', packetSchema, { method: 'POST', body });

/**
 * Email a copy of a filed step's signed PDF (`PKT-21`, ADR-0008). A person
 * presses Send; nothing sends one by itself.
 */
export const sendCopy = (packetId: string, stepOrder: number, body: CopyRequest) =>
  request(`/api/v1/packets/${packetId}/steps/${String(stepOrder)}/copy`, copySentSchema, {
    method: 'POST',
    body,
  });

export const cancelledSchema = z.object({
  id: z.string(),
  stage: z.literal('cancelled'),
});

/**
 * Cancel a packet that is not complete (`PKT-27`). Steps not yet started are
 * never started; steps already started carry on as ordinary agreements, and
 * nothing is withdrawn from signature. A reason is required (`PKT-30`), and is
 * shown on the packet's page alone.
 */
export const cancelPacket = (packetId: string, reason: string) =>
  request(`/api/v1/packets/${packetId}/cancel`, cancelledSchema, {
    method: 'POST',
    body: { reason: reason.trim() },
  });

export const deletedPacketSchema = z.object({
  id: z.string(),
  reference: z.string(),
  /** The references of the agreements its steps became, deleted with it. */
  agreements: z.array(z.string()),
  /** Whether a step of it was sent, so it is kept on record rather than deleted outright. */
  removed: z.boolean(),
});

/**
 * Delete a cancelled packet, with the agreements its steps became: its
 * runner's, or an Admin's. One a step of which was sent for signature no
 * longer appears anywhere in Vellum and is kept on record. The server refuses
 * one with a step still out for signature or signed, saying why, and the
 * record that each existed survives (`DOC-9`).
 */
export const deletePacket = (packetId: string) =>
  request(`/api/v1/packets/${packetId}`, deletedPacketSchema, { method: 'DELETE' });

export const stepSkippedSchema = z.object({
  step_order: z.number(),
  /** Whether that was the last step unfinished, so the packet is complete. */
  packet_complete: z.boolean(),
});

/**
 * Skip a step whose document was cancelled (`PK-01`): it counts as done, and
 * the packet moves on to the next. To do the step after all, start it again
 * from the launch form instead. The server decides who may.
 */
export const skipStep = (packetId: string, stepOrder: number) =>
  request(
    `/api/v1/packets/${packetId}/steps/${String(stepOrder)}/skip`,
    stepSkippedSchema,
    { method: 'POST' },
  );

export const handedOverSchema = z.object({
  id: z.string(),
  /** Whether the new owner's notice went. The handover stands either way. */
  notified: z.boolean(),
  /** The Slack direct message, where one was asked for (`PKT-39`). */
  slack: z.enum(['sent', 'failed', 'unavailable']).nullable().default(null),
  /** What to do about a Slack message that did not go, in plain words. */
  slack_problem: z.string().nullable().default(null),
});

export type HandedOver = z.infer<typeof handedOverSchema>;

/**
 * Hand a packet to a colleague, who takes it over (`PKT-34`): they run it from
 * here, and the person who handed it over no longer sees it. A colleague by
 * id, never a typed address. They are told in Vellum and by email (`PKT-36`).
 */
export const handOverPacket = (packetId: string, userId: string, slack = false) =>
  request(`/api/v1/packets/${packetId}/hand-over`, handedOverSchema, {
    method: 'POST',
    body: { user_id: userId, slack },
  });

/**
 * Bundles: create, edit, remove.
 *
 * A packet copies its bundle's steps when it starts, so an edit here changes
 * only packets started afterwards (`PKT-1`).
 */
export const createBundle = (body: BundleInput) =>
  request('/api/v1/packet-workflows', packetWorkflowSchema, { method: 'POST', body });

export const updateBundle = (id: string, body: BundleInput) =>
  request(`/api/v1/packet-workflows/${id}`, packetWorkflowSchema, {
    method: 'PUT',
    body,
  });

export const removedBundleSchema = z.object({
  id: z.string(),
  name: z.string(),
  outcome: z.enum(['deleted', 'retired']),
});

/** Deleted if no packet came from it, retired otherwise. The server decides. */
export const removeBundle = (id: string) =>
  request(`/api/v1/packet-workflows/${id}`, removedBundleSchema, { method: 'DELETE' });

/**
 * Who a packet waits on, step by step (`REM-12`): each step's undecided
 * approvers and the signers whose turn it is, with the same standing the
 * agreement page reads -- whether each can be reminded now, and when they
 * last were.
 */
export const waitingPersonSchema = z.object({
  kind: z.enum(['approver', 'signer']),
  id: z.string(),
  name: z.string(),
  can_remind: z.boolean(),
  last_reminded_at: z.string().nullable(),
  last_reminded_by: z.string().nullable(),
  next_reminder_at: z.string().nullable(),
});

export type WaitingPerson = z.infer<typeof waitingPersonSchema>;

export const waitingStepSchema = z.object({
  step_order: z.number(),
  agreement_id: z.string(),
  reference: z.string(),
  stage: z.string(),
  people: z.array(waitingPersonSchema),
});

export type WaitingStep = z.infer<typeof waitingStepSchema>;

export const packetWaitingSchema = z.object({
  steps: z.array(waitingStepSchema),
  can_remind_everyone: z.boolean(),
});

export type PacketWaiting = z.infer<typeof packetWaitingSchema>;

export const packetWaitingQuery = (id: string) =>
  queryOptions({
    queryKey: ['packet-waiting', id] as const,
    queryFn: ({ signal }) =>
      request(`/api/v1/packets/${id}/waiting`, packetWaitingSchema, { signal }),
  });

/**
 * Remind everybody the packet waits on, across its steps, skipping anybody
 * the hourly or daily rule would refuse (`REM-13`). The answer says how many
 * were reminded, and how many were skipped and why.
 */
export const remindPacket = (id: string) =>
  request(`/api/v1/packets/${id}/remind`, reminderBatchSchema, { method: 'POST' });
